import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Package,
  AlertTriangle,
  CheckCircle,
  MapPin,
  Snowflake,
  Clock,
  XCircle,
} from "lucide-react";
import type { MaterialInventory } from "@/types/legacy";
import { cn } from "@/lib/utils";

interface MaterialKanbanViewProps {
  materials: MaterialInventory[];
  onSelectMaterial: (id: string) => void;
}

interface KanbanColumn {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  items: MaterialInventory[];
}

export function MaterialKanbanView({ materials, onSelectMaterial }: MaterialKanbanViewProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const columns = useMemo<KanbanColumn[]>(() => {
    // Clasificar materiales
    const inUse: MaterialInventory[] = [];
    const expired: MaterialInventory[] = [];
    const finished: MaterialInventory[] = [];
    const byLocation: Record<string, MaterialInventory[]> = {};

    materials.forEach((material) => {
      // Verificar si está caducado
      if (material.expiry_date) {
        const expiryDate = new Date(material.expiry_date);
        if (expiryDate < today) {
          expired.push(material);
          return;
        }
      }

      // Verificar si está agotado o inactivo
      if (material.current_stock <= 0 || !material.is_active) {
        finished.push(material);
        return;
      }

      // Verificar si es material de congelador (en uso)
      if (material.is_freezer_material) {
        inUse.push(material);
        return;
      }

      // Agrupar por ubicación
      const location = material.storage_location || material.location || "Sin ubicación";
      if (!byLocation[location]) {
        byLocation[location] = [];
      }
      byLocation[location].push(material);
    });

    const result: KanbanColumn[] = [
      {
        id: "in-use",
        title: "En Uso (Congeladores)",
        icon: <Snowflake className="w-4 h-4" />,
        color: "text-blue-600",
        bgColor: "bg-blue-500/10 border-blue-500/30",
        items: inUse,
      },
      {
        id: "expired",
        title: "Caducados",
        icon: <AlertTriangle className="w-4 h-4" />,
        color: "text-red-600",
        bgColor: "bg-red-500/10 border-red-500/30",
        items: expired,
      },
      {
        id: "finished",
        title: "Agotados / Inactivos",
        icon: <XCircle className="w-4 h-4" />,
        color: "text-gray-600",
        bgColor: "bg-gray-500/10 border-gray-500/30",
        items: finished,
      },
    ];

    // Agregar columnas por ubicación
    Object.entries(byLocation).forEach(([location, items]) => {
      result.push({
        id: `location-${location}`,
        title: location,
        icon: <MapPin className="w-4 h-4" />,
        color: "text-purple-600",
        bgColor: "bg-purple-500/10 border-purple-500/30",
        items,
      });
    });

    return result;
  }, [materials, today]);

  const getDaysUntilExpiry = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <ScrollArea className="w-full">
      <div className="flex gap-4 pb-4 min-w-max">
        {columns.map((column) => (
          <div
            key={column.id}
            className={cn(
              "flex-shrink-0 w-72 rounded-lg border-2",
              column.bgColor
            )}
          >
            <div className={cn("flex items-center gap-2 p-3 border-b", column.color)}>
              {column.icon}
              <h3 className="font-semibold text-sm">{column.title}</h3>
              <Badge variant="secondary" className="ml-auto">
                {column.items.length}
              </Badge>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="p-2 space-y-2">
                {column.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Sin materiales
                  </div>
                ) : (
                  column.items.map((material) => {
                    const isLowStock = material.current_stock < material.min_stock;
                    const daysUntilExpiry = material.expiry_date
                      ? getDaysUntilExpiry(material.expiry_date)
                      : null;

                    return (
                      <Card
                        key={material.id}
                        className="cursor-pointer hover:shadow-md transition-shadow bg-card"
                        onClick={() => onSelectMaterial(material.id)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-medium text-sm line-clamp-2">
                              {material.name}
                            </h4>
                            {!material.is_active && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                Inactivo
                              </Badge>
                            )}
                          </div>

                          {material.code && (
                            <p className="text-xs text-muted-foreground mb-2">
                              {material.code}
                            </p>
                          )}

                          <div className="flex items-center justify-between text-xs mb-2">
                            <span className="text-muted-foreground">Stock:</span>
                            <span
                              className={cn(
                                "font-medium",
                                isLowStock ? "text-yellow-600" : "text-green-600"
                              )}
                            >
                              {material.current_stock} {material.unit}
                            </span>
                          </div>

                          {material.category && (
                            <Badge variant="outline" className="text-xs mb-2">
                              {material.category}
                            </Badge>
                          )}

                          {daysUntilExpiry !== null && (
                            <div
                              className={cn(
                                "flex items-center gap-1 text-xs mt-2",
                                daysUntilExpiry < 0
                                  ? "text-red-600"
                                  : daysUntilExpiry < 30
                                  ? "text-yellow-600"
                                  : "text-muted-foreground"
                              )}
                            >
                              <Clock className="w-3 h-3" />
                              {daysUntilExpiry < 0
                                ? `Caducado hace ${Math.abs(daysUntilExpiry)} días`
                                : daysUntilExpiry === 0
                                ? "Caduca hoy"
                                : `Caduca en ${daysUntilExpiry} días`}
                            </div>
                          )}

                          {isLowStock && (
                            <div className="flex items-center gap-1 text-xs text-yellow-600 mt-2">
                              <AlertTriangle className="w-3 h-3" />
                              Stock bajo
                            </div>
                          )}

                          {material.supplier && (
                            <p className="text-xs text-muted-foreground mt-2 truncate">
                              {material.supplier}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
